<template>
    <hox-modal :show-footer="canDuplicateCampaign" @close="close">
        <hox-loading-layer v-if="isLoadingDuplicateCampaign || isLoadingCampaign" :is-full-screen="false" />

        <template #header>Duplicate campaign</template>

        <hox-alert v-if="hasErrorLoadingCampaign" margin-bottom="none" type="danger">
            <template #title>Uh oh!</template>
            <template #content>
                <p>Something went wrong while getting some data. Try again in a few moments.</p>
            </template>
            <template #actionItems>
                <Button type="primary" @click="refetchCampaign">Retry</Button>
            </template>
        </hox-alert>

        <hox-alert v-else-if="masterTemplateOptions.length === 0" margin-top="base" type="warning">
            <template #content>
                <p>The campaign you have selected cannot be duplicated as it has no master templates.</p>
            </template>
        </hox-alert>

        <template v-else>
            <CampaignForm
                v-if="initialFormData"
                ref="form"
                :disable-wpp-project-id="isWppLogin"
                :initial-form-data="initialFormData"
                :sections="sections"
                :has-creative-x-enabled="hasCreativeXEnabled"
                :client-creative-x-secret-id="clientCreativeXSecretId"
                :has-satalia-enabled="hasSataliaEnabled"
                @hasValidationErrorsUpdate="hasFormValidationErrors = $event"
                @submit="duplicateCampaign"
            />

            <div class="checkbox-group-wrapper">
                <label>Master Templates</label>

                <CheckboxGroup v-model="selectedMasterTemplates">
                    <Checkbox v-for="option in masterTemplateOptions" :key="option.value" :label="option.value">
                        {{ option.label }}
                    </Checkbox>
                </CheckboxGroup>

                <p v-for="error in validationErrors.selectedMasterTemplates" :key="error" class="error-wrapper">
                    {{ error }}
                </p>
            </div>

            <div class="checkbox-group-wrapper">
                <label>Languages</label>

                <CheckboxGroup v-model="selectedLanguages">
                    <Checkbox
                        v-for="option in languageOptions"
                        :key="option.value"
                        :label="option.value"
                        :disabled="option.value === 'en'"
                    >
                        {{ option.label }}
                        <span v-if="option.value === 'en'">(Required)</span>
                    </Checkbox>
                </CheckboxGroup>

                <p v-for="error in validationErrors.selectedLanguages" :key="error" class="error-wrapper">
                    {{ error }}
                </p>
            </div>

            <hox-alert margin-bottom="none" margin-top="large" type="info">
                <template #content>
                    <p>
                        Duplicating a campaign will create a new campaign with the selected master templates and
                        languages, along with all creative content belonging to them.
                    </p>

                    <p>
                        Teams members, annotations, QA status and published status will not be brought over to the new
                        campaign.
                    </p>
                </template>
            </hox-alert>

            <p v-for="error in mutationErrors" :key="error" class="error-wrapper">Error: {{ error }}</p>
        </template>

        <template #footer>
            <Button
                type="primary"
                :disabled="isLoadingDuplicateCampaign || hasValidationErrors"
                data-testid="duplicate-campaign-modal__button--duplicate"
                @click="submit"
            >
                Duplicate
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { ApolloError } from "@apollo/client/core";

import CampaignForm, { type CampaignFormData } from "@/components/CampaignForm.vue";

import campaignWithLanguagesAndMasterTemplatesQuery from "@/apollo/queries/v2/CampaignWithLanguagesAndMasterTemplates.gql";
import copyCampaignMutation from "@/apollo/mutations/v2/CopyCampaign.gql";

import { generateMasterTemplateFilterOptions } from "@/utils";

import type { CampaignSection } from "@/types/types";

import { AuthGetters } from "@/store/modules/auth";
import getDefaultCreativeXCampaignSettings from "@/utils/CreativeX/getDefaultCreativeXCampaignSettings";
import getDefaultSataliaCampaignSettings from "@/utils/Satalia/getDefaultSataliaCampaignSettings";
import { localeToFriendlyName, type LanguageCode } from "@/utils/languages";

export default defineComponent({
    components: {
        CampaignForm
    },

    props: {
        campaignId: {
            required: true,
            type: String
        },

        sections: {
            required: true,
            type: Array as PropType<CampaignSection[]>
        },

        hasCreativeXEnabled: {
            type: Boolean,
            default: false
        },

        hasConnectEnabled: {
            type: Boolean,
            default: false
        },

        clientCreativeXSecretId: {
            type: String,
            required: false
        },

        hasSataliaEnabled: {
            type: Boolean,
            default: false
        }
    },

    emits: ["close", "campaignDuplicated"],

    data() {
        return {
            initialFormData: null as Partial<CampaignFormData> | null,
            hasErrorLoadingCampaign: false,
            hasFormValidationErrors: false,
            isLoadingCampaign: 0,
            isLoadingDuplicateCampaign: false,
            languageOptions: [] as { label: string; value: string }[],
            masterTemplateOptions: [] as { width: number; height: number; value: string; label: string }[],
            selectedLanguages: [] as string[],
            selectedMasterTemplates: [] as string[],
            mutationErrors: [] as string[],
            isWppLogin: false
        };
    },

    computed: {
        canDuplicateCampaign() {
            return this.masterTemplateOptions.length > 0;
        },

        hasValidationErrors() {
            return this.hasFormValidationErrors || Object.keys(this.validationErrors).length > 0;
        },

        validationErrors() {
            const errors = {} as Record<string, string[]>;

            if (this.selectedLanguages.length === 0) {
                errors.selectedLanguages = ["At least 1 language needs to be selected."];
            }

            if (this.selectedMasterTemplates.length === 0) {
                errors.selectedMasterTemplates = ["At least 1 master template needs to be selected."];
            }

            return errors;
        }
    },

    mounted() {
        this.isWppLogin = this.$store.getters[AuthGetters.isWppLogin];
    },

    methods: {
        close() {
            this.$emit("close");
        },

        refetchCampaign() {
            this.$apollo.queries.campaign.refetch();
        },

        submit() {
            (this.$refs.form as HTMLFormElement).submit();
        },

        async duplicateCampaign(formData: CampaignFormData) {
            if (this.hasValidationErrors || this.isLoadingDuplicateCampaign) {
                return;
            }

            this.isLoadingDuplicateCampaign = true;
            this.mutationErrors = [];

            try {
                await this.$apollo.mutate({
                    mutation: copyCampaignMutation,
                    variables: {
                        campaignId: this.campaignId,
                        languages: this.selectedLanguages,
                        masterTemplateIds: this.selectedMasterTemplates,

                        name: formData.name,
                        jiraTicketUrl: formData.jiraTicketUrl,
                        sectionId: formData.sectionId === "none" ? null : formData.sectionId,
                        toneOfVoice: formData.toneOfVoice,
                        defaultTimelineLength: +formData.defaultTimelineLength,
                        wppProjectId: formData.wppProjectId,
                        ...(this.hasConnectEnabled && {
                            opendcPlatformSettings: formData.opendcPlatformSettings
                        })
                    }
                });

                this.$snackbar.success("Campaign duplicated");
                this.$emit("campaignDuplicated");
                this.close();
            } catch (error) {
                const graphQLErrors = (error as ApolloError)?.graphQLErrors || [];
                this.mutationErrors = graphQLErrors.map(({ message }) => message);
            } finally {
                this.isLoadingDuplicateCampaign = false;
            }
        }
    },

    apollo: {
        campaign: {
            query: campaignWithLanguagesAndMasterTemplatesQuery,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingCampaign",
            variables() {
                return {
                    campaignId: this.campaignId
                };
            },
            result({ data: { campaign }, error }) {
                if (error) {
                    this.hasErrorLoadingCampaign = true;
                    return;
                }

                this.hasErrorLoadingCampaign = false;

                this.initialFormData = {
                    name: `Copy of ${campaign.name}`,
                    jiraTicketUrl: campaign.jiraTicketUrl,
                    sectionId: campaign.section?._id,
                    toneOfVoice: campaign.toneOfVoice,
                    defaultTimelineLength: campaign.defaultTimelineLength,
                    wppProjectId: campaign.wppProjectId,
                    ...(this.hasCreativeXEnabled && {
                        creativeXPlatformSettings: {
                            ...getDefaultCreativeXCampaignSettings(),
                            ...campaign.creativeXPlatformSettings
                        }
                    }),
                    ...(this.hasConnectEnabled && {
                        opendcPlatformSettings: campaign.opendcPlatformSettings
                    }),
                    ...(this.hasSataliaEnabled && {
                        sataliaPlatformSettings: {
                            ...getDefaultSataliaCampaignSettings(),
                            ...campaign.sataliaPlatformSettings
                        }
                    })
                };

                this.languageOptions = campaign.languages.map((language: LanguageCode) => ({
                    label: localeToFriendlyName(language),
                    value: language
                }));

                this.selectedLanguages = [...campaign.languages];
                this.masterTemplateOptions = generateMasterTemplateFilterOptions(campaign.masterTemplates);
                this.selectedMasterTemplates = this.masterTemplateOptions.map(({ value }) => value);
            }
        }
    }
});
</script>

<style scoped lang="css">
.checkbox-group-wrapper {
    margin-top: 12px;

    .ivu-checkbox-group-item {
        display: block;
    }
}

.error-wrapper {
    color: red;
    margin-top: 12px;
}
</style>
